import React, { useState, useEffect, useMemo } from 'react'
import {
  Box, Typography, CircularProgress, Alert,
} from '@mui/material'
import { apiService } from '../../services/api'
import AgentDrillDown from './AgentDrillDown'
import {
  ReportShell, HeaderButton, FilterBar, KpiStrip, Section, ReportTable, SubTabs, Icon, fmt,
  useDistinctFilters, buildOplFilterFields,
} from '../report-shell'

const monthNames = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
]

const OrangeLocatorDeviceSales = () => {
  const now = new Date()
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())
  const [polkoweOnly, setPolkoweOnly] = useState(false)
  const [activeKamTab, setActiveKamTab] = useState(0)
  const [filters, setFilters] = useState({})

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [exporting, setExporting] = useState(false)

  const fetchData = async (currentFilters = filters) => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiService.getOrangeLocatorDeviceSales(
        selectedMonth, selectedYear, polkoweOnly, currentFilters,
      )
      setData(response.data)
      setActiveKamTab(0)
    } catch (err) {
      setError(`Błąd pobierania danych: ${err.message}`)
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, polkoweOnly])

  const onFiltersChange = (next) => {
    setFilters(next)
    fetchData(next)
  }

  const exportExcel = async () => {
    try {
      setExporting(true)
      const response = await apiService.getOrangeLocatorDeviceSalesExport(selectedMonth, selectedYear, polkoweOnly)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      const suffix = polkoweOnly ? '_polkowe' : ''
      link.href = url
      link.setAttribute('download', `Lokalizator_Sprzedaz_Urzadzen_${selectedYear}-${String(selectedMonth).padStart(2, '0')}${suffix}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(`Błąd eksportu: ${err.message}`)
    } finally {
      setExporting(false)
    }
  }

  const kamTabs = useMemo(() => {
    if (!data) return ['Wszyscy']
    return ['Wszyscy', ...data.kam_summary.map(k => k.kam)]
  }, [data])

  const filteredSalons = useMemo(() => {
    if (!data) return []
    if (activeKamTab === 0) return data.salons
    const selectedKam = kamTabs[activeKamTab]
    return data.salons.filter(s => (s.kam || '').toUpperCase().trim() === selectedKam)
  }, [data, activeKamTab, kamTabs])

  const columns = useMemo(() => {
    const cols = [
      { field: 'kod_bscs', header: 'Kod BSCS', width: '1.4fr', minWidth: 180 },
      { field: 'region', header: 'Region', width: '140px' },
      { field: 'district', header: 'Dystrykt', width: '140px' },
    ]
    if (activeKamTab === 0) {
      cols.push({ field: 'kam', header: 'KAM', width: '110px' })
    }
    cols.push(
      { field: 'city', header: 'Miasto', width: '140px' },
      { field: 'ch', header: 'CH', width: '140px' },
      { field: 'watch_goat', header: 'Watch Goat', width: '120px', sales: true },
      { field: 'watch_luna', header: 'Watch Luna', width: '120px', sales: true },
      { field: 'gps_dog', header: 'GPS Dog', width: '110px', sales: true },
      { field: 'total', header: 'Total', width: '100px', sales: true },
    )
    return cols
  }, [activeKamTab])

  const rows = useMemo(() => (
    filteredSalons.map((s, idx) => ({ id: s.plant_code || s.kod_bscs || idx, ...s }))
  ), [filteredSalons])

  const years = []
  const currentYear = new Date().getFullYear()
  for (let y = currentYear; y >= currentYear - 2; y--) years.push(y)

  const distinct = useDistinctFilters('opl')
  const periods = [
    {
      type: 'select', key: 'month', label: 'Miesiąc',
      value: selectedMonth, display: monthNames[selectedMonth - 1],
      options: monthNames.map((n, i) => ({ value: i + 1, label: n })),
      onChange: (v) => setSelectedMonth(parseInt(v)),
      allowClear: false,
    },
    {
      type: 'select', key: 'year', label: 'Rok',
      value: selectedYear, display: String(selectedYear),
      options: years.map(y => ({ value: y, label: String(y) })),
      onChange: (v) => setSelectedYear(parseInt(v)),
      allowClear: false,
    },
  ]
  const filterFields = [
    ...buildOplFilterFields(distinct, filters, onFiltersChange),
    {
      type: 'toggle', key: 'polkoweOnly', label: 'Tylko salony półkowe',
      value: polkoweOnly, onChange: setPolkoweOnly,
    },
  ]

  const totals = data?.totals || {}
  const kpiTiles = data ? [
    { key: 'salons', label: 'Salony',     value: fmt.int(totals.salon_count || 0) },
    { key: 'goat',   label: 'Watch Goat', value: fmt.int(totals.watch_goat  || 0), accent: '#2563eb' },
    { key: 'luna',   label: 'Watch Luna', value: fmt.int(totals.watch_luna  || 0), accent: '#7c3aed' },
    { key: 'dog',    label: 'GPS Dog',    value: fmt.int(totals.gps_dog     || 0), accent: '#d97706' },
    { key: 'total',  label: 'Razem',      value: fmt.int(totals.total       || 0), accent: '#16a34a' },
  ] : []

  return (
    <ReportShell
      title="Orange — Lokalizator · Sprzedaż per urządzenie"
      subtitle="Rozbicie sprzedaży Lokalizatora na Watch Goat, Watch Luna i GPS Dog dla wybranego miesiąca."
      breadcrumb={['KAM-SFA', 'Orange', 'Lokalizator', 'Sprzedaż per urządzenie']}
      accentColor="#ff7900"
      actions={
        <HeaderButton primary onClick={exportExcel} disabled={exporting || !data}>
          <Icon.download /> {exporting ? 'Eksport…' : 'Eksport Excel'}
        </HeaderButton>
      }
      filterBar={
        <FilterBar
          periods={periods}
          filters={filterFields}
          onReset={() => onFiltersChange({})}
        />
      }
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {(() => {
            const tabsItems = kamTabs.map((tab, i) => {
              if (i === 0) return { value: 0, label: `Wszyscy (${data.totals.salon_count})` }
              const ks = data.kam_summary.find(k => k.kam === tab)
              return { value: i, label: `${tab} (${ks?.salon_count || 0})` }
            })
            return (
              <Section
                title="Salony"
                right={<SubTabs tabs={tabsItems} value={activeKamTab} onChange={setActiveKamTab} />}
              >
                <ReportTable
                  rows={rows}
                  columns={columns}
                  initialSort={{ field: 'total', dir: 'desc' }}
                  pageSize={25}
                  pageSizeOptions={[25, 50, 100]}
                  density="compact"
                />
              </Section>
            )
          })()}

          {/* Per-Agent drill-down (tylko gdy mamy dane z orange_pos_authorization) */}
          {data.agent_summary && data.agent_summary.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Sprzedaż per Agent (rozwiń, by zobaczyć salony)
              </Typography>
              {data.agent_summary.slice(0, 30).map((a) => (
                <AgentDrillDown
                  key={a.agent}
                  agent={a.agent || '(brak agenta)'}
                  operator="orange"
                  summary={{
                    rks: a.rksa,
                    salon_count: a.salon_count,
                    sales: a.total,
                    pos_type: a.siec_sprzedazy,
                  }}
                  filters={{
                    agent: a.agent && a.agent !== 'BRAK' ? a.agent : undefined,
                    rksa: filters.rksa || undefined,
                    pos_type: filters.pos_type || undefined,
                    region: filters.region || undefined,
                    dystrykt: filters.dystrykt || undefined,
                    xdm: filters.xdm || undefined,
                  }}
                />
              ))}
            </Box>
          )}
        </>
      ) : null}
    </ReportShell>
  )
}

export default OrangeLocatorDeviceSales
